export interface SubtopicState {
  id: string
  name: string
  mastery: number
  stabilityDays: number
  difficulty: number
  retrievability: number
  effectiveMastery: number
  lastAttemptAt: string | null
  isEnabled?: boolean
  minProficiencyThreshold?: number
  maxProficiencyThreshold?: number
  rollupWeight?: number
  slug?: string
}

export interface TopicState {
  id: string
  name: string
  chapter: number | null
  minProficiencyThreshold: number
  maxProficiencyThreshold: number
  topicMastery: number
  prerequisiteIds: string[]
  prerequisitesMet: boolean
  includedInPlan: boolean
  slug?: string
  subtopics: SubtopicState[]
}

export interface ReportedApproach {
  type: string
  isPreselected: boolean
  otherText?: string
}

export interface CompletedWorkItem {
  id: string
  problemSlug: string
  problemTitle: string
  source: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  topicName: string
  subtopicName: string
  completedAt: string
  durationMinutes: number
  runCount: number
  submitCount: number
  selfScore: 1 | 2 | 3 | 4 | null
  reportedApproach: ReportedApproach | null
  needsGrading: boolean
  code: string
  knownApproaches: string[]
}

export interface RecommendationReason {
  label: string
  detail: string
}

export interface ScoreboardEntry {
  label: string
  value: number
  max: number
}

export type RecommendationTrack = 'baseline' | 'adjustment'
export type RecommendationStatus =
  | 'suggested'
  | 'assigned'
  | 'postponed'
  | 'rejected'

export interface RecommendationItem {
  id: string
  rank: number
  problemSlug: string
  problemTitle: string
  source: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  topicId: string
  topicName: string
  subtopicId: string
  subtopicName: string
  track: RecommendationTrack
  status: RecommendationStatus
  reasons: RecommendationReason[]
  scoreboard: ScoreboardEntry[]
  statusChangedAt: string
}

export type CalculationSource =
  | 'attempt'
  | 'coach_adjustment'
  | 'engine_recompute'
  | 'seed'
  | 'recommendation_lifecycle'

export type SourceTable =
  | 'AttemptEventLog'
  | 'CoachAdjustmentLog'
  | 'ClientSubtopicState'
  | 'ClientTopicState'
  | 'ClientRecommendation'

export type ActorKind = 'student' | 'coach' | 'system'
export type CalculationLogEntryKind = 'original' | 'replay'
export type CalculationTargetKind =
  | 'subtopic'
  | 'topic'
  | 'overall'
  | 'projection'

export type ReasonCode =
  | 'student_attempt_easy'
  | 'student_attempt_good'
  | 'student_attempt_hard'
  | 'student_attempt_again'
  | 'threshold_change'
  | 'rollup_weight_change'
  | 'preset_applied'
  | 'target_company_change'
  | 'baseline_level_change'
  | 'topic_inclusion_change'
  | 'subtopic_enable_toggle'
  | 'weekly_commitment_change'
  | 'target_date_change'
  | 'attempt_reviewed'
  | 'coach_seed'
  | 'coach_seed_override'
  | 'fsrs_recompute'
  | 'decay_catchup'
  | 'coach_revert'
  | 'coach_reapply'
  | 'coach_revert_to_snapshot'
  | 'reprocess_cascade'
  | 'processing_run_requested'
  | 'settings_snapshot_captured'
  | 'baseline_epoch_change'
  | 'graph_json_saved'

export const REASON_CODES: readonly ReasonCode[] = [
  'student_attempt_easy',
  'student_attempt_good',
  'student_attempt_hard',
  'student_attempt_again',
  'threshold_change',
  'rollup_weight_change',
  'preset_applied',
  'target_company_change',
  'baseline_level_change',
  'topic_inclusion_change',
  'subtopic_enable_toggle',
  'weekly_commitment_change',
  'target_date_change',
  'attempt_reviewed',
  'coach_seed',
  'coach_seed_override',
  'fsrs_recompute',
  'decay_catchup',
  'coach_revert',
  'coach_reapply',
  'coach_revert_to_snapshot',
  'reprocess_cascade',
  'processing_run_requested',
  'settings_snapshot_captured',
  'baseline_epoch_change',
  'graph_json_saved',
] as const

export interface CalculationWeight {
  symbol: string
  value: string
  role: string
}

export interface CalculationRowChange {
  targetKind: CalculationTargetKind
  targetName: string
  field: string
  before: number
  after: number
  formulaName: string
  purpose: string
  equation: string
  weights: CalculationWeight[]
  inputs: { name: string; value: string; note: string }[]
  output: string
}

export interface SnapshotDiffRow {
  label: string
  previousValue: string
  currentValue: string
}

export interface BaseCalculationLogEntry {
  id: string
  occurredAt: string
  source: CalculationSource
  kind: CalculationLogEntryKind
  replacesEntryIds: string[]
  sourceTable: SourceTable
  sourceRowId: string
  actorKind: ActorKind
  actorName: string
  reasonCode: ReasonCode
  rationale: string
  summary: string
  rowsAffected: CalculationRowChange[]
  snapshotDiff?: SnapshotDiffRow[]
}

export type BaseCalcLogEntry = BaseCalculationLogEntry

export interface AttemptCalculationLogEntry extends BaseCalculationLogEntry {
  source: 'attempt'
  problemSlug: string
  problemSource: string
  grade: 1 | 2 | 3 | 4
  durationMinutes: number
  runCount: number
  submitCount: number
}

export interface CoachAdjustmentCalculationLogEntry
  extends BaseCalculationLogEntry {
  source: 'coach_adjustment'
  coachUserId?: string | null
  valueBefore?: unknown
  valueAfter?: unknown
  graphJsonReasonMetadata?: {
    role: 'origin' | 'propagated'
    originClientId?: string
    originCoachUserId?: string | null
    originCoachName?: string
    originAdjustmentId?: string
  }
}

export interface EngineRecomputeCalculationLogEntry
  extends BaseCalculationLogEntry {
  source: 'engine_recompute'
}

export interface SeedCalculationLogEntry extends BaseCalculationLogEntry {
  source: 'seed'
}

export interface RecommendationLifecycleCalculationLogEntry
  extends BaseCalculationLogEntry {
  source: 'recommendation_lifecycle'
}

export type CalculationLogEntry =
  | AttemptCalculationLogEntry
  | CoachAdjustmentCalculationLogEntry
  | EngineRecomputeCalculationLogEntry
  | SeedCalculationLogEntry
  | RecommendationLifecycleCalculationLogEntry

export type CalculationStatus = 'applied' | 'reverted'
